/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useEffect, useRef } from 'react';
import { useQuery } from '@apollo/react-hooks';

import { InlineCommentMap } from '@atlaskit/editor-plugin-annotation';

import {
	updateCommentHighlightsInEditor,
	getEditorResolvedState,
	getSortedMarkerRefs,
} from '@confluence/comments-util';
import { fg } from '@confluence/feature-gating';

import {
	CommentType,
	GraphQLContentStatus,
	Depth,
} from './__types__/useCommentRefetchInlineCommentTopLevelMarkerRefsQuery';
import type {
	useCommentRefetchInlineCommentTopLevelMarkerRefsQuery as useCommentRefetchInlineCommentTopLevelMarkerRefsQuery$data,
	useCommentRefetchInlineCommentTopLevelMarkerRefsQueryVariables as useCommentRefetchInlineCommentTopLevelMarkerRefsQuery$variables,
} from './__types__/useCommentRefetchInlineCommentTopLevelMarkerRefsQuery';

const useCommentRefetchInlineCommentTopLevelMarkerRefsQuery = gql`
	query useCommentRefetchInlineCommentTopLevelMarkerRefsQuery(
		$pageId: ID!
		$contentStatus: [GraphQLContentStatus]
		$depth: Depth
		$type: [CommentType]
	) {
		comments(pageId: $pageId, contentStatus: $contentStatus, depth: $depth, type: $type) {
			nodes {
				id
				parentId
				location {
					type
					... on InlineComment {
						inlineMarkerRef
					}
				}
			}
		}
	}
`;

// Extract filtering logic for testability
export const filterAnnotationsByAdf = (
	annotations: string[],
	adfAnnotations: InlineCommentMap | undefined,
): string[] => {
	if (!adfAnnotations) {
		return [];
	}
	return annotations.filter((annotation) => {
		return adfAnnotations.hasOwnProperty(annotation);
	});
};

type CommentRefetchProps = {
	isDocumentActive: boolean;
	contentId: string;
	triggerAnalyticsEvent: (
		eventName: string,
		extraData?: {
			[key: string]: any;
		},
	) => Promise<void>;
	updateUnresolvedInlineComment: (unresolvedInlineComments: string[]) => void;
	handleGqlError: (error: Error) => void;
	previousAnnotations: string[];
	updateUnreadCommentsOnFirstRender: (shouldUpdateUnreadCommentsOnFirstRender: boolean) => void;
	updateUnreadComments: (shouldUpdateUnreadComments: boolean) => void;
	adfAnnotations: InlineCommentMap | undefined;
};

export const useCommentRefetch = ({
	isDocumentActive,
	contentId,
	triggerAnalyticsEvent,
	updateUnresolvedInlineComment,
	handleGqlError,
	previousAnnotations,
	updateUnreadCommentsOnFirstRender,
	updateUnreadComments,
	adfAnnotations,
}: CommentRefetchProps) => {
	const { refetch } = useQuery<
		useCommentRefetchInlineCommentTopLevelMarkerRefsQuery$data,
		useCommentRefetchInlineCommentTopLevelMarkerRefsQuery$variables
	>(useCommentRefetchInlineCommentTopLevelMarkerRefsQuery, {
		variables: {
			pageId: contentId,
			contentStatus: [GraphQLContentStatus.DRAFT, GraphQLContentStatus.CURRENT],
		},
		skip: true,
		onError: handleGqlError,
		fetchPolicy: 'network-only',
	});

	// Track previous isDocumentActive state to detect when document becomes active
	const prevIsDocumentActive = useRef(isDocumentActive);

	useEffect(() => {
		// Only trigger refetch when document becomes active (user comes back to page)
		if (isDocumentActive && !prevIsDocumentActive.current) {
			refetch({
				pageId: contentId,
				contentStatus: [GraphQLContentStatus.DRAFT, GraphQLContentStatus.CURRENT],
				depth: Depth.ROOT,
				type: [CommentType.UNRESOLVED],
			})
				.then((response) => {
					const updatedAnnotationsFromQuery = updateCommentHighlightsInEditor({
						data: response.data,
						previousAnnotations,
					});

					// organizes the annotations for navigation to work
					if (updatedAnnotationsFromQuery.length) {
						// filter out annotations from the response that are not in the annotation state
						// the query may return dangling annotations that are not in the ADF
						const updatedAnnotationsWithAdf = fg('confluence_frontend_new_dangling_comments_ux')
							? filterAnnotationsByAdf(updatedAnnotationsFromQuery, adfAnnotations)
							: updatedAnnotationsFromQuery;

						// annotations is a sorted list of marker refs in the ADF
						void getEditorResolvedState({
							annotations: getSortedMarkerRefs(updatedAnnotationsWithAdf, 'edit'),
							pageId: contentId,
							triggerAnalyticsEvent,
							updateUnresolvedInlineComment,
						});

						updateUnreadCommentsOnFirstRender(true);
						updateUnreadComments(true);
					}
				})
				.catch((error) => {
					handleGqlError(error);
				});
		}

		// Update the ref with current state
		prevIsDocumentActive.current = isDocumentActive;
		// we only want to update the comment highlights when the user comes back to the page
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDocumentActive, adfAnnotations]);
};
